import React, { createContext, useCallback, useRef, useState } from 'react';
import { Loading } from '@goodfynd/ui';
import { useRouter } from 'next/router';
import Custom500 from 'pages/500';

import routes from '../../config/routes';
import { useAuth } from '../../hooks/useAuth';
import useUser from '../../hooks/useUser';
import type { UserInfo } from '../../stores/models/user';

import type {
  AppContextValue,
  AppDispatchContextValue,
  AppProviderProps,
} from './types';

export const AppContext = createContext<AppContextValue | undefined>(undefined);
export const AppDispatchContext = createContext<
  AppDispatchContextValue | undefined
>(undefined);

export default function AppProvider({ children }: AppProviderProps) {
  const router = useRouter();

  const defaultUserValue = useRef<UserInfo>({
    isAuthenticated: undefined,
    isGuest: true,
  } as UserInfo);
  const [user, setUser] = useState<UserInfo>(defaultUserValue.current);
  const setGuest = () => {
    setUser(defaultUserValue.current);
  };

  const notFound = useCallback(() => {
    router.push(routes.account.profile);
  }, [router]);

  const {
    getLoginUrl,
    isAuthenticated,
    isLoading: identityLoading,
    logout,
    user: identity,
  } = useAuth();

  const {
    error: userError,
    isFetched: userIsFetched,
    isLoading: userIsLoading,
    refetch: refreshAuth,
  } = useUser({ enabled: isAuthenticated, setUser, setGuest, notFound });

  const renderContent = useCallback(() => {
    if (identityLoading || userIsLoading) {
      return <Loading type="line" />;
    }

    if (userError) {
      return <Custom500 />;
    }

    return children;
  }, [identityLoading, userIsLoading, userError, children]);

  return (
    <AppContext.Provider
      value={{
        identity,
        identityLoading,
        isLoggedIn: isAuthenticated,
        user,
        userHasError: userIsFetched ? !!userError : undefined,
        userIsFetched,
        userIsLoading,
      }}
    >
      <AppDispatchContext.Provider
        value={{
          getLoginUrl,
          logout,
          refreshAuth,
          setUser,
        }}
      >
        {renderContent()}
      </AppDispatchContext.Provider>
    </AppContext.Provider>
  );
}
